import { get, noop, set } from 'lodash/fp';

import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  RESET_CHALLENGE,
  SET_PLAYERLIST_SORT,
  SET_POLL_DURATION,
  SHARE_CHALLENGE_FAILURE,
  SHARE_CHALLENGE_REQUEST,
  SHARE_CHALLENGE_SUCCESS,
  SUBMIT_CHALLENGE_SUCCESS,
  UPDATE_ENTITY_PROPERTY,
  UPDATE_PLAYERS,
  TOGGLE_ENTITY_PROPERTY,
  RESET_FANTASY_INSIDER_PROJECTIONS
} from '../constants/actionTypes';
import { IN_PROGRESS } from '../constants/challengeStatusTypes';
import { OPEN_CHALLENGE_POLL_DURATION } from '../constants/pollDurations';
import decodeChallenge from '../normalizers/decodeChallenge';
import { CREATE_CHALLENGE, CHALLENGE, H2H_CHALLENGE, PRIZES } from '../constants/apiTypes';
import { isInProgress } from '../utils/challengeUtils';

const initialState = {
  bonuses: {},
  competitionId: null,
  competitionIcon: null,
  competitionName: null,
  cost: null,
  countries: [],
  discount: 0,
  entryCap: null,
  entryCount: null,
  errors: [],
  gameCount: 0,
  games: [],
  hasEntered: false,
  hasInitialised: false,
  hasProjections: false,
  hasWon: false,
  id: null,
  isChallengeBuilder: false,
  isDrop1: false,
  isFeatured: false,
  isH2H: false,
  isLoading: false,
  isLocked: false,
  isRacing: false,
  isRapidfire: false,
  isTargetChallenge: false,
  isTicketChallenge: false,
  isTrainingChallenge: false,
  isUnlimited: false,
  isVisible: false,
  labels: [],
  link: null,
  maxEntriesPerPerson: 1,
  myEntries: [],
  name: null,
  onesportCompetitionId: null,
  onesportSportId: null,
  order: 0,
  ownerType: null,
  payout: null,
  payoutIcon: null,
  payoutLongDescription: null,
  payoutMessage: null,
  payoutType: null,
  playerPositions: {},
  playerQuota: 0,
  players: {},
  playersCount: [],
  playersPerSideCap: 0,
  playersPerGame: undefined,
  pollDuration: OPEN_CHALLENGE_POLL_DURATION,
  pollTimestamp: 0,
  prize: null,
  prizePool: null,
  prizePoolValue: null,
  prizes: [],
  query: null,
  racingType: undefined,
  rank: 0,
  rapidfireRuledOutPlayers: [],
  rapidfireTiers: [],
  reducedCost: 0,
  responseStatusCode: 0,
  salaryCap: null,
  score: 0,
  scoring: [],
  shareChallengeErrorMessages: [],
  sharingChallenge: false,
  sharingMessages: {},
  showPlayerSelections: false,
  slateId: 0,
  sortDirection: 'desc',
  sortField: 'salary',
  sportIcon: null,
  sportId: null,
  sportName: null,
  sports: [],
  startTime: null,
  statusId: 0,
  statusLabel: '',
  sticky: false,
  submittedChallengeEntryId: null,
  subSportId: null,
  teamPositions: {},
  teamQuota: 0,
  teams: {},
  userEntries: [],
  userEntryCount: 0,
  userEntryIds: [],
  winnings: null
};

const apiRequestReducer = switchFunc(
  {
    [CHALLENGE]: (state, action) => ({
      errors: [],
      hasInitialised: state.hasInitialised && action.id === state.id,
      id: action.id,
      isLoading: !action.quiet,
      responseStatusCode: 0
    }),
    [H2H_CHALLENGE]: () => ({
      id: null,
      errors: [],
      hasInitialised: false,
      isLoading: true
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [CHALLENGE]: (state, action) => {
      const challengeData = decodeChallenge(action.data, action.meta);

      const beginLive = state.statusId < IN_PROGRESS && challengeData.statusId >= IN_PROGRESS;

      let sortField = beginLive ? 'score' : state.sortField;
      let sortDirection = beginLive ? 'desc' : state.sortDirection;

      if (
        challengeData.isRacing &&
        challengeData.statusId < IN_PROGRESS &&
        sortField === 'salary'
      ) {
        sortField = 'number';
        sortDirection = 'asc';
      }

      return {
        ...challengeData,
        hasInitialised: true,
        hasProjections:
          !!get('projected_score_enabled', action.meta) && isInProgress(challengeData.statusId),
        isLoading: false,
        pollTimestamp: action.timestamp,
        responseStatusCode: get('status', action) || state.responseStatusCode,
        sortDirection,
        sortField
      };
    },
    [H2H_CHALLENGE]: (state, action) => {
      const challengeData = decodeChallenge(action.data, action.meta);

      return {
        ...challengeData,
        // id: action.id,
        slateId: action.id,
        entryCap: 2,
        entryCount: 0,
        hasInitialised: true,
        isLoading: false,
        multiEntry: true,
        gameCount: action.data.games.length,
        sortField: challengeData.isRacing ? 'number' : state.sortField,
        sortDirection: challengeData.isRacing ? 'asc' : state.sortDirection
      };
    },
    [CREATE_CHALLENGE]: () => initialState,
    [PRIZES]: (state, action) => ({
      prizes: action.data
    })
  },
  () => noop
);

const apiFailureReducer = switchFunc(
  {
    [CHALLENGE]: (state, action) => ({
      errors: get('errors.messages', action),
      isLoading: false,
      pollTimestamp: action.timestamp,
      responseStatusCode: get('status', action) || state.responseStatusCode
    }),
    [H2H_CHALLENGE]: (state, action) => ({
      errors: get('errors.messages', action),
      isLoading: false
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequestReducer(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccessReducer(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailureReducer(action.api)(state, action),
  [SET_PLAYERLIST_SORT]: (state, action) => ({
    sortDirection:
      action.sortField === state.sortField && state.sortDirection === 'desc' ? 'asc' : 'desc',
    sortField: action.sortField
  }),
  [SET_POLL_DURATION]: (state, action) => ({
    pollDuration: action.duration
  }),
  [SHARE_CHALLENGE_REQUEST]: () => ({
    sharingChallenge: true,
    shareChallengeErrorMessages: []
  }),
  [SHARE_CHALLENGE_SUCCESS]: () => ({
    sharingChallenge: false
  }),
  [SHARE_CHALLENGE_FAILURE]: (state, action) => ({
    sharingChallenge: false,
    shareChallengeErrorMessages: action.errors.messages
  }),
  [UPDATE_ENTITY_PROPERTY]: (state, { entity, field, value }) => {
    if (entity.substring(0, 7) === 'PLAYER_') {
      return {
        players: set([entity.substring(7), field], value, state.players)
      };
    }
    if (entity.substring(0, 5) === 'TEAM_') {
      return {
        teams: set([entity.substring(5), field], value, state.teams)
      };
    }
    return null;
  },
  [TOGGLE_ENTITY_PROPERTY]: (state, { entity, field }) => {
    if (entity.substring(0, 7) === 'PLAYER_') {
      const playerId = entity.substring(7);
      return {
        players: set([playerId, field], !get(field, state.players[playerId]), state.players)
      };
    }
    if (entity.substring(0, 5) === 'TEAM_') {
      const teamId = entity.substring(5);
      return {
        teams: set([teamId, field], !get(field, state.teams[teamId]), state.teams)
      };
    }
    return null;
  },
  [UPDATE_PLAYERS]: (state, { players }) => {
    const insiderPlayers = players;
    const newPlayers = {};
    const newTeams = {};

    Object.entries(state.players).forEach(([id, player]) => {
      newPlayers[id] = { ...player, ...get(`PLAYER_${id}`, insiderPlayers) };
    });

    Object.entries(state.teams).forEach(([id, team]) => {
      newTeams[id] = { ...team, ...get(`TEAM_${id}`, insiderPlayers) };
    });

    return {
      players: newPlayers,
      teams: newTeams
    };
  },
  [RESET_FANTASY_INSIDER_PROJECTIONS]: state => {
    const newPlayers = {};
    const newTeams = {};

    Object.entries(state.players).forEach(([id, player]) => {
      newPlayers[id] = { ...player, insiderScoreUpdated: player.insiderScore };
    });

    Object.entries(state.teams).forEach(([id, team]) => {
      newTeams[id] = { ...team, insiderScoreUpdated: team.insiderScore };
    });
    return {
      players: newPlayers,
      teams: newTeams
    };
  },
  [SUBMIT_CHALLENGE_SUCCESS]: (state, action) => ({
    hasEntered: true,
    userEntryIds: [...state.userEntryIds, action.data.id],
    submittedChallengeEntryId: action.data.id
  }),
  [RESET_CHALLENGE]: () => initialState
});
